import { dictionaryApi } from './dictionaryClient';

export type DictionaryMedia = {
  id: number;
  subantaId?: number | null;
  tingantaId?: number | null;
  uchharan: string;
  audio?: string | null;
  video?: string | null;
  photo?: string | null;
  link?: string | null;
};

export type CreateMediaBody =
  | {
      subantaId: number;
      uchharan: string;
      audio?: string | null;
      video?: string | null;
      photo?: string | null;
      link?: string | null;
    }
  | {
      tingantaId: number;
      uchharan: string;
      audio?: string | null;
      video?: string | null;
      photo?: string | null;
      link?: string | null;
    };

export type PatchMediaBody = Partial<
  Pick<DictionaryMedia, 'uchharan' | 'audio' | 'video' | 'photo' | 'link'>
>;

function normalizeList(data: unknown): DictionaryMedia[] {
  if (Array.isArray(data)) return data as DictionaryMedia[];
  if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: DictionaryMedia[] }).data;
  }
  return [];
}

export function isDictionaryMedia(x: unknown): x is DictionaryMedia {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.id === 'number' &&
    typeof o.uchharan === 'string'
  );
}

export function mediaBelongsToEntry(
  m: DictionaryMedia,
  variant: 'subanta' | 'tinganta',
  parentId: number
): boolean {
  if (variant === 'subanta') return m.subantaId === parentId;
  return m.tingantaId === parentId;
}

/** Parse nested relation: API may return one object, an array, or empty. */
export function parseMediaForEntry(
  raw: unknown,
  variant: 'subanta' | 'tinganta',
  parentId: number
): DictionaryMedia | null {
  if (raw == null) return null;
  const candidates = Array.isArray(raw) ? raw : [raw];
  for (const item of candidates) {
    if (isDictionaryMedia(item) && mediaBelongsToEntry(item, variant, parentId)) {
      return item;
    }
  }
  return null;
}

export async function listMedia(params: {
  subantaId?: number;
  tingantaId?: number;
}): Promise<DictionaryMedia[]> {
  const res = await dictionaryApi.get<unknown>('/media', { params });
  return normalizeList(res.data);
}

export async function getMedia(id: number): Promise<DictionaryMedia> {
  const res = await dictionaryApi.get<DictionaryMedia>(`/media/${id}`);
  return res.data;
}

export async function createMedia(body: CreateMediaBody): Promise<DictionaryMedia> {
  const res = await dictionaryApi.post<DictionaryMedia>('/media', body);
  return res.data;
}

export async function updateMedia(id: number, body: PatchMediaBody): Promise<DictionaryMedia> {
  const res = await dictionaryApi.patch<DictionaryMedia>(`/media/${id}`, body);
  return res.data;
}

export async function deleteMedia(id: number): Promise<void> {
  await dictionaryApi.delete(`/media/${id}`);
}

/** Turn stored path or absolute URL into a browser-usable URL (API origin + `/uploads/...`). */
export function resolveMediaAssetUrl(path: string | null | undefined): string | null {
  const p = path == null ? null : String(path).trim();
  if (!p) return null;
  if (/^https?:\/\//i.test(p)) return p;
  const base = String(dictionaryApi.defaults.baseURL ?? '').replace(/\/$/, '');
  return p.startsWith('/') ? `${base}${p}` : `${base}/${p}`;
}

type UploadParent =
  | { subantaId: number; tingantaId?: never }
  | { tingantaId: number; subantaId?: never };

function appendUploadForm(fd: FormData, parent: UploadParent, uchharan: string, file: File) {
  fd.append('file', file);
  fd.append('uchharan', uchharan);
  if ('subantaId' in parent) {
    fd.append('subantaId', String(parent.subantaId));
  } else {
    fd.append('tingantaId', String(parent.tingantaId));
  }
}

export async function uploadMediaAudio(
  args: UploadParent & { file: File; uchharan: string }
): Promise<DictionaryMedia> {
  const fd = new FormData();
  appendUploadForm(fd, args, args.uchharan, args.file);
  const res = await dictionaryApi.post<DictionaryMedia>('/media/upload/audio', fd);
  return res.data;
}

export async function uploadMediaVideo(
  args: UploadParent & { file: File; uchharan: string }
): Promise<DictionaryMedia> {
  const fd = new FormData();
  appendUploadForm(fd, args, args.uchharan, args.file);
  const res = await dictionaryApi.post<DictionaryMedia>('/media/upload/video', fd);
  return res.data;
}

export async function uploadMediaPhoto(
  args: UploadParent & { file: File; uchharan: string }
): Promise<DictionaryMedia> {
  const fd = new FormData();
  appendUploadForm(fd, args, args.uchharan, args.file);
  const res = await dictionaryApi.post<DictionaryMedia>('/media/upload/photo', fd);
  return res.data;
}

function normOpt(s: string | null | undefined): string | null {
  if (s == null) return null;
  const t = String(s).trim();
  return t === '' ? null : t;
}

export type MediaFormScalars = {
  uchharan: string;
  audio: string;
  video: string;
  photo: string;
  link: string;
};

export function buildMediaPatchFromForm(prev: DictionaryMedia, next: MediaFormScalars): PatchMediaBody {
  const patch: PatchMediaBody = {};
  const u = next.uchharan.trim();
  if (prev.uchharan !== u) patch.uchharan = u;
  const na = normOpt(next.audio);
  if (normOpt(prev.audio) !== na) patch.audio = na;
  const nv = normOpt(next.video);
  if (normOpt(prev.video) !== nv) patch.video = nv;
  const np = normOpt(next.photo);
  if (normOpt(prev.photo) !== np) patch.photo = np;
  const nl = normOpt(next.link);
  if (normOpt(prev.link) !== nl) patch.link = nl;
  return patch;
}
